import databaseFieldList from "./databaseFieldList.json";

const actions = {};

const mutations = {};

// Fields that are required and cannot be hidden from the form
const requiredFields = [
  "templateType",
  "documentationStatus",
  "careSetting",
  "applicationDropdownValueId",
  "primaryActivityDropdownValueId",
  "packId",
  "title",
  "userRole",
  "operationalizeType",
  "releaseType",
  "qaAnalyst",
];

// Mapping of field names to human-readable labels
// Extracted from databaseFieldList.json to maintain single source of truth
// Filters out legacy fields (excludeFromForm=true) that exist only for audit logs
const fieldLabels = Object.keys(databaseFieldList.releaseNote)
  .filter(key => !databaseFieldList.releaseNote[key].excludeFromForm)
  .reduce((acc, key) => {
    acc[key] = databaseFieldList.releaseNote[key].label;
    return acc;
  }, {});

const state = {
  list: {
    breakFix: {
      name: "Break/Fix",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "issue",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    communicationOnly: {
      name: "Communication Only",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "currentBehavior",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    fyi: {
      name: "FYI",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    newBuild: {
      name: "New Build",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "isPulled",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    optionalEnhancement: {
      name: "Optional Enhancement",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "isPulled",
        "application",
        "operationalizeType",
        "optionalEnhancement",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    updateBuild: {
      name: "Update Build",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "currentBehavior",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "isPulled",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    rte: {
      name: "UAT with Real Patients",
      fields: generateFields([
        "activitiesImpacted",
        "buildAnalyst",
        "careSetting",
        "documentationStatus",
        "applicationDropdownValueId",
        "isPublished",
        "isPulled",
        "application",
        "newBehavior",
        "operationalizeType",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "jiraSupportAnalyst",
        "reopenedReason",
        "requiredSetup",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
    all: {
      name: "All",
      fields: generateFields([
        "activitiesImpacted",
        "addedPostUat",
        "background",
        "buildAnalyst",
        "buildJira",
        "buildType",
        "currentBehavior",
        "careSetting",
        "documentationStatus",
        "documentedOnBehalfOf",
        "applicationDropdownValueId",
        "isPublished",
        "isPulled",
        "issue",
        "lastUpdated",
        "application",
        "newBehavior",
        "note",
        "operationalizeType",
        "optionalEnhancement",
        "otherUserRoles",
        "preGoLiveSetup",
        "primaryActivity",
        "primaryActivityDropdownValueId",
        "pulledDate",
        "pulledNextSteps",
        "pulledReason",
        "qaAnalyst",
        "qaJira",
        "releaseId",
        "releaseType",
        "reopenedReason",
        "jiraSupportAnalyst",
        "requiredSetup",
        "serviceAreaAttributeList",
        "status",
        "summary",
        "templateType",
        "title",
        "trainingJira",
        "uatJira",
        "updatedReason",
        "userRole",
      ]),
    },
  },
};

function generateFields(includedFields = []) {
  const fields = {
    activitiesImpacted: false,
    addedPostUat: false,
    background: false,
    buildAnalyst: false,
    buildJira: false,
    buildType: false,
    currentBehavior: false,
    careSetting: false,
    documentationStatus: false,
    documentedOnBehalfOf: false,
    applicationDropdownValueId: false,
    primaryActivityDropdownValueId: false,
    isPublished: false,
    isPulled: false,
    issue: false,
    lastUpdated: false,
    // application: false, // REMOVED - Legacy field excluded from form
    newBehavior: false,
    note: false,
    operationalizeType: false,
    optionalEnhancement: false,
    otherUserRoles: false,
    preGoLiveSetup: false,
    // primaryActivity: false, // REMOVED - Legacy field excluded from form
    pulledDate: false,
    pulledNextSteps: false,
    pulledReason: false,
    qaAnalyst: false,
    qaJira: false,
    releaseId: false,
    releaseType: false,
    reopenedReason: false,
    jiraSupportAnalyst: false,
    requiredSetup: false,
    serviceAreaAttributeList: false,
    status: false,
    summary: false,
    templateType: false,
    title: false,
    trainingJira: false,
    uatJira: false,
    updatedReason: false,
    userRole: false,
  };

  includedFields.forEach(field => {
    fields[field] = true;
  });

  return fields;
}

const getters = {
  fieldLabels: () => fieldLabels,
  requiredFields: () => requiredFields,
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
